// Canonical formatters shared across the dashboard.
//
// Policy: null / undefined → "—". Zero is a real measurement and renders as a
// formatted zero (e.g. "$0.00", "0.00%"). Always two decimal places for
// currency and percent. Whole numbers for counts (fmtNumber) unless the caller
// passes forceDecimals.

const isMissing = (v) => v == null;

export const fmtCurrency = (v, { dropCents = false } = {}) => {
  if (isMissing(v)) return "—";
  const n = Number(v);
  if (dropCents && Math.abs(n) >= 1000) {
    return `$${Math.round(n).toLocaleString()}`;
  }
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// fmtPercent — INPUT IS A RATIO. Multiplies by 100: 0.27 → "27.00%".
// Use this for values that arrive as a 0–1 fraction (GA4 bounce_rate,
// engagement_rate, etc.).
//
// ⚠️ CONTRACT TRAP: this is NOT interchangeable with the per-feature
// `fmtPct` helpers in competitive-intelligence/ and event-intelligence/,
// which take an ALREADY-PERCENT value (27 → "27%") and do NOT multiply.
// Passing a percent (27) here yields "2700.00%"; passing a ratio (0.27) to
// those yields "0.3%". If a call site has an already-percent number, use
// fmtPctValue below — never fmtPercent.
export const fmtPercent = (v) => {
  if (isMissing(v)) return "—";
  return `${(Number(v) * 100).toFixed(2)}%`;
};

// fmtPctValue — INPUT IS ALREADY A PERCENT. Does NOT multiply: 27 → "27.00%".
// The shared counterpart to CI's `fmtPct` / EI's `fmtPct` (which use 1 and 0
// decimals respectively). Provided so a call site holding a percent-valued
// number has a correctly-named shared option and never reaches for fmtPercent
// (the ratio-input one) by mistake. Default 2 decimals; pass `digits` to match
// a feature's precision.
export const fmtPctValue = (v, digits = 2) => {
  if (isMissing(v)) return "—";
  return `${Number(v).toFixed(digits)}%`;
};

export const fmtRatio = (v) => {
  if (isMissing(v)) return "—";
  return `${Number(v).toFixed(2)}x`;
};

// fmtNumber renders counts. Pass forceDecimals=true to show one decimal when
// the value has a non-integer part (used for conversion counts where Google Ads
// returns fractional values like 12.5 due to attribution modeling).
export const fmtNumber = (v, forceDecimals = false) => {
  if (isMissing(v)) return "—";
  const n = Number(v);
  if (forceDecimals && n % 1 !== 0) {
    return n.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  }
  return Math.round(n).toLocaleString();
};

// fmtCount is the locale-grouped count used in flowing insight copy (e.g.
// "1,204 bookings"). Unlike fmtNumber it renders a MISSING value as "0", not "—",
// because in a sentence "0 scored events" reads correctly where "— scored events"
// does not — and it does NOT round (the callers pass already-rounded counts).
// Was defined identically in the Google Ads + Event Intelligence VerdictCards and
// the EI PipelineHealthPanel; consolidated here. (CI's own `fmtNum` renders
// missing → "—" and is intentionally kept separate in competitive-intelligence/shared.)
export const fmtCount = (value) => Number(value || 0).toLocaleString();

// fmtDate returns YYYY-MM-DD in local time (used for API params and filenames).
// Returns "" for invalid input so callers can compose filenames safely.
export const fmtDate = (d) => {
  try {
    const dt = typeof d === "string" ? new Date(d) : d;
    if (Number.isNaN(dt?.getTime?.())) return "";
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch {
    return "";
  }
};

// Human-readable date label, e.g. "Mon, Jan 5". Accepts a Date, a bare
// "YYYY-MM-DD" string, or a full timestamp.
//
// A BARE DATE is parsed as local midnight to avoid the UTC-shift gotcha — but
// only a bare one. Appending "T00:00:00" unconditionally turned every timestamp
// that already carried a time into "2026-08-25T00:12:17+00:00T00:00:00", which
// is an Invalid Date, so the label fell through to the em dash. Every
// `started_at` this app reads is a full timestamp, and the conversation detail
// pane — where a dash for a present field is explicitly forbidden — rendered one
// on every row.
const BARE_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const fmtDateLabel = (d) => {
  if (isMissing(d)) return "—";
  const dt = typeof d === "string"
    ? new Date(BARE_DATE.test(d) ? `${d}T00:00:00` : d)
    : d;
  if (Number.isNaN(dt?.getTime?.())) return "—";
  return dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};

/**
 * Humanise a stored enum or key for display: "communication_style" → "Communication style".
 *
 * Distinct from `titleCase`, which capitalises every word and leaves underscores in
 * place — that renders "Communication_Style" for a snake_case key.
 */
/**
 * A `tel:` URI for a value that is a phone number, or null when it is not one.
 *
 * Digits only in the href — punctuation in a `tel:` is tolerated by most diallers
 * but not all. Requires at least 7 digits so a site number or a party size is
 * never turned into something tappable.
 */
export const telHref = (value) => {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!/^[+(]?[\d][\d\s().-]{5,}$/.test(raw)) return null;
  const digits = raw.replace(/[^\d+]/g, '');
  return digits.replace(/\D/g, '').length >= 7 ? `tel:${digits}` : null;
};

/**
 * Display form of a US phone number for visible text.
 *
 * Local-intelligence answers ship phones in mixed formats — "(928) 567-6725"
 * (already formatted) or "+1 928-567-6725" (needs formatting). This normalises
 * only numbers that start with "+1": it strips the "+1" prefix, takes the 10
 * remaining digits, and reformats them as "(XXX) XXX-XXXX". Numbers that do
 * not start with "+1" (already-formatted or international) are returned
 * unchanged. If stripping "+1" does not leave exactly 10 digits, the original
 * string is returned so a malformed value shows verbatim rather than a wrong
 * number. Display only — never use the result for a `tel:` href; `telHref`
 * keeps the raw digits for dialing.
 */
export const formatPhone = (value) => {
  if (value == null) return value;
  const raw = String(value).trim();
  if (!raw.startsWith('+1')) return value;
  const digits = raw.slice(2).replace(/\D/g, '');
  if (digits.length !== 10) return value;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/**
 * Export-header form of a key: "conversation_id" → "CONVERSATION ID".
 *
 * Spreadsheet headers and file titles read as data-dictionary entries when the
 * raw column name goes through unchanged; underscores are a storage detail.
 */
export const upperLabel = (value) =>
  String(value ?? '')
    .replace(/_/g, ' ')
    .trim()
    .toUpperCase();

export const labelFor = (value) =>
  String(value ?? '')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());

// UPPERCASE / lowercase / mixed → "Title Case". Used for guest names and other
// user-visible identifiers we surface from `v_booking_enriched` / MySQL exports
// which land here as full-uppercase (e.g. "RAMON MALAVE SANTIAGO"). Correctly
// uppercases the letter after hyphens ("MARY-JANE" → "Mary-Jane") and
// apostrophes ("O'BRIEN" → "O'Brien"), and handles accented Unicode
// ("JOSÉ MARÍA" → "José María"). Deliberately leaves "Mc"/"Mac" prefixes as
// literal Title Case — over-eager rules ("McDonald") cause more damage than
// they prevent on real-world data. Preserves null/empty as-is so callers can
// render their own placeholder.
export const titleCase = (str) => {
  if (str == null) return str;
  const s = String(str);
  if (!s) return s;
  // `\b` in JS regex is ASCII-only even with the `u` flag, so use a Unicode-
  // aware lookbehind: match any letter that is NOT preceded by a letter or
  // combining mark, then upper-case it.
  return s.toLowerCase().replace(/(?<![\p{L}\p{M}])\p{L}/gu, (c) => c.toUpperCase());
};

// Seconds → "M:SS". Used for engagement-time metrics.
export const fmtDuration = (seconds) => {
  if (isMissing(seconds)) return "—";
  const s = Math.round(Number(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

// Seconds → hours-aware compact duration: "1h 30m", "10h", "45m", "20s".
// For time-TRACKING surfaces (effort/timesheet) where fmtDuration's "M:SS" reads
// wrong at hours scale — a 90-minute entry as "90:00" is ambiguous, and a 10h
// one as "600:00" more so. Drops the minutes segment when it is zero. Missing → "—".
export const fmtDurationHM = (seconds) => {
  if (isMissing(seconds)) return "—";
  const total = Math.max(0, Math.round(Number(seconds)));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  if (m > 0) return `${m}m`;
  return `${total}s`;
};

// Seconds → "HH:MM:SS" — the running-clock / timesheet duration format
// ("00:05:00", "05:00:00"). Hours are NOT capped at 24, so a multi-day entry still
// reads correctly. Missing → "—".
export const fmtClock = (seconds) => {
  if (isMissing(seconds)) return "—";
  const total = Math.max(0, Math.round(Number(seconds)));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

// A timestamp → "10:30AM" (12-hour, no space, uppercase meridiem) for a timesheet
// time-of-day. Accepts a Date or an ISO string. Missing/invalid → "—".
export const fmtTimeOfDay = (d) => {
  if (isMissing(d)) return "—";
  const dt = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(dt?.getTime?.())) return "—";
  return dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }).replace(/\s/g, "");
};

// fmtCappedPercent — INPUT IS A RATIO (0–1), but the rate is never allowed to
// exceed 100%. Voice Intelligence spec: "rates capped at 100%". A ratio above 1
// (e.g. a recovery rate that the backend computed as 1.04 on a tiny base) is
// shown as 100%, never 104% — the capped value is the honest representation of
// "everyone was recovered". Missing → "—".
export const fmtCappedPercent = (v) => {
  if (isMissing(v)) return "—";
  const pct = Math.min(100, Number(v) * 100);
  return `${pct.toFixed(2)}%`;
};

// fmtPeopleOrPercent — "below ten people show the raw count, never a
// percentage" (Voice Intelligence spec). A percentage of a group smaller than
// ten is misleading ("100% of 2 callers"). When `count` is under ten, render
// the raw count (e.g. "3"); otherwise render the rate as a capped percent of
// `ratio` (e.g. "27.00%"). Both branches are honest representations of the same
// fact. `count` missing → "—".
//
// `ratio` is the 0–1 fraction that becomes the percentage when the group is
// large enough. If the caller holds an already-percent number, pass it as a
// ratio by dividing by 100, or use fmtCappedPercent directly when no count gate
// applies.
export const fmtPeopleOrPercent = (count, ratio) => {
  if (isMissing(count)) return "—";
  const n = Number(count);
  if (n < 10) return fmtCount(n);
  return fmtCappedPercent(ratio);
};

// ── Hub due / timing formatters (moved here from hub/_shims/formatters.js, Epic 0)
// The spec composes due/timing into the server-side `says` string, so these are
// intentionally minimal. They live in the canonical shared formatter module now
// (one source, not a shim duplicate) so the ClientSlideOut / timeline surfaces
// Epic 1 builds can import them from here.

/**
 * Compact absolute fallback for an ISO `at` timestamp.
 * @param {string|Date|null|undefined} iso
 * @returns {string}
 */
export const formatWhen = (iso) => {
  if (isMissing(iso)) return "";
  // Strings and epoch numbers parse; a Date is used as-is. Anything else (an object, an
  // array, a boolean) is not a date, so it reads as missing instead of throwing.
  const d = typeof iso === "string" || typeof iso === "number" ? new Date(iso) : iso;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

/**
 * A conditional promise ("once you approve the copy") carries a condition instead
 * of a date — render it as-is, never as missing data.
 * @param {{due?: string|null, dueCondition?: string|null}} [arg]
 * @returns {string}
 */
export const formatDue = ({ due, dueCondition } = {}) => {
  if (dueCondition) return dueCondition;
  return formatWhen(due);
};

// ── Work-page formatters (moved here from WorkPage.js / hub/work utils) ────────
// These were inline in WorkPage.js (and duplicated in hub/work/utils.js). They now
// live in the canonical shared formatter module so WorkPage imports them by name
// instead of redefining them. The work components still import fmtDays /
// fmtDisplayDate / cap / titleCase from hub/work/utils.js for now — re-pointing
// those is a follow-up. fmtNum is intentionally NOT added here: WorkPage's local
// fmtNum (`Number(v ?? 0).toLocaleString()`, missing → "0") is semantically
// fmtCount (already exported above), so WorkPage's fmtNum call sites are renamed
// to fmtCount rather than introducing a second, semantically-misleading fmtNum.

/** Capitalize first letter, lowercase rest. */
export const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

/** Format a duration in days. Missing → "—", 0 → "today", 1 → "1 day". */
export const fmtDays = (days) => {
  if (days == null) return "—";
  if (days === 0) return "today";
  if (days === 1) return "1 day";
  return `${fmtCount(days)} days`;
};

/** Format a YYYY-MM-DD string for display without timezone conversion. */
export const fmtDisplayDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric", year: "numeric",
  });
};

/** Compact due-date label for a row — "Jun 11" (no weekday/year). */
export const fmtDueDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/** Forecast-confidence opacity class — "low" dims, else full. */
export const confidenceOpacity = (confidence) => {
  if (confidence === "low") return "opacity-60";
  return "opacity-100";
};

/**
 * className for a Work filter control: neutral by default, or a blue tint
 * (border-blue-500/50 + bg-blue-500/10) when the filter is narrowed from its
 * default. Rounded-rectangle (rounded-lg) shape with hover, dark-mode palette.
 */
export const filterPillClass = (isActive) =>
  isActive
    ? "flex items-center gap-1.5 h-9 rounded-lg px-3 text-sm border border-blue-400 bg-blue-50 text-blue-700 dark:border-blue-500/50 dark:bg-blue-500/10 dark:text-blue-300 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-500/20"
    : "flex items-center gap-1.5 h-9 rounded-lg px-3 text-sm border border-gray-200 bg-white text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700";
